import React, { useState } from 'react';
import { useSelector } from 'react-redux';
import { makeDropdownOptions } from '../../../../helper/helper';
import { CharacterType } from '../../../../config/story.config';
import { CharacterFormProps } from '../../models/CharacterFormProps.interface';
import { StoryCharacterAgeUnitEnum, StoryCharacterEnum } from '../../../../interfaces/character.interface';
import {
  eyeColorOptions,
  heirColorOptions,
  SelectorOptionModel,
  skinToneOptions,
} from './CharacterDescriptionInputs.model';
import Uploader from '../Uploader';
import { SecureImg } from '../../../../components/Common/SecureImage';
import { RootState } from '../../../../store/store';

const CharacterForm: React.FC<CharacterFormProps> = ({
  handleImageChange,
  characterOutlineByUser,
  errors,
  handleInputChange,
  storyData,
  previewImage,
  setPreviewImage,
  selectedImage,
  currentPortrait,
  setShouldCoverPortrait,
  continueWithoutImage,
  onBlurSave,
}) => {
  const [hoveredOnImage, setHoveredOnImage] = useState<boolean>(false);
  // Mark the description (`mics`) field as in-error when the most recent
  // character generation was rejected by a moderation layer. The full
  // guidance is shown in the inline red banner below the portrait area;
  // this adds a matching red treatment on the field the user must edit.
  const hasModerationError = useSelector(
    (state: RootState) => !!state.app.bookData?.characterContentFilter?.flagged,
  );

  const generateListOptions = (options: SelectorOptionModel[] | undefined | null) => {
    return (
      options &&
      options?.length > 0 &&
      options.map((option: SelectorOptionModel) => (
        <option value={option.value} key={option.value}>
          {option.title}
        </option>
      ))
    );
  };

  return (
    <div className="flex flex-col gap-2 mx-auto w-[18rem] md:w-[25rem]">
      <div className=" bg-white rounded-[1.5rem] flex flex-col gap-4 overflow-y-auto">
        <div className="w-full flex flex-col gap-4 px-4 pt-2 pb-6">
          <div className="flex flex-col gap-1 mt-2">
            <div className="flex gap-2 justify-start items-center">
              <label htmlFor="characterType" className="w-40 text-start text-sm font-bold text-storyblue">
                Character Type:
                {<span className="text-red-500 text-left text-xs italic">* </span>}
              </label>
              <select
                id="characterType"
                name="characterType"
                value={characterOutlineByUser?.characterType || ''}
                required
                onChange={(e) => {
                  handleInputChange(e);
                }}
                className="w-full h-9 text-center border-gray-300 border rounded-md focus:ring-indigo-500 focus:border-indigo-500"
              >
                {makeDropdownOptions(CharacterType())}
              </select>
            </div>
            {errors?.characterType && (
              <div className="text-red-500 text-left text-xs italic">{errors?.characterType}</div>
            )}
          </div>

          {!(
            characterOutlineByUser?.characterType === StoryCharacterEnum.animal ||
            characterOutlineByUser?.characterType === StoryCharacterEnum.fictional
          ) && (
            <>
              <div className="flex flex-col gap-1">
                <div className="flex w-full gap-2">
                  <label
                    htmlFor="age"
                    className="w-24 h-9 text-center font-bold text-sm text-storyblue flex items-center"
                  >
                    Age: <span className="text-red-500 text-xs italic ">* </span>
                  </label>
                  <input
                    id="age"
                    name="age"
                    type="number"
                    max="100"
                    min="1"
                    value={characterOutlineByUser?.age ?? ''}
                    onChange={(e) => {
                      handleInputChange(e);
                    }}
                    onBlur={() => onBlurSave?.()}
                    required
                    className="w-[33%] text-center border-gray-300 border rounded-md focus:ring-indigo-500 focus:border-indigo-500 ml-3 "
                  />
                  <select
                    id="ageUnit"
                    name="ageUnit"
                    value={characterOutlineByUser?.ageUnit || StoryCharacterAgeUnitEnum.year}
                    onChange={(e) => {
                      handleInputChange(e);
                    }}
                    required
                    className="w-[33%] text-center border-gray-300 border rounded-md focus:ring-indigo-500 focus:border-indigo-500"
                  >
                    <option value={StoryCharacterAgeUnitEnum.year}>Years</option>
                    <option value="month">Months</option>
                  </select>
                </div>
                {errors?.age && <div className="text-red-500 text-left text-xs italic">{errors?.age}</div>}
              </div>
              <div className="flex flex-col gap-1">
                <div className="flex gap-2 justify-start items-center">
                  <label htmlFor="gender" className="w-40 text-start text-sm font-bold text-storyblue">
                    Gender: <span className="text-red-500 text-left text-xs italic ">* </span>
                  </label>
                  <select
                    id="gender"
                    name="gender"
                    value={characterOutlineByUser?.gender || ''}
                    required={continueWithoutImage}
                    onChange={(e) => {
                      handleInputChange(e);
                    }}
                    className="w-full h-9 text-center border-gray-300 border rounded-md focus:ring-indigo-500 focus:border-indigo-500"
                  >
                    <option value=""></option>
                    <option value="boy">Boy</option>
                    <option value="girl">Girl</option>
                  </select>
                </div>
                {errors?.gender && <div className="text-red-500 text-left text-xs italic">{errors?.gender}</div>}
              </div>
            </>
          )}

          {characterOutlineByUser?.characterType === StoryCharacterEnum.animal && (
            <div className="flex flex-col gap-1">
              <div className="flex gap-2 justify-start items-center">
                <label htmlFor="animalType" className="w-40 text-start text-sm font-bold text-storyblue">
                  Animal Type:
                  <span className="text-red-500  text-xs italic ">* </span>
                </label>
                <textarea
                  name="animalType"
                  rows={2}
                  className="w-full max-h-10 rounded-md text-sm p-2 border border-gray-300"
                  value={characterOutlineByUser.animalType || ''}
                  required
                  onChange={(e) => handleInputChange(e)}
                  onBlur={() => onBlurSave?.()}
                  placeholder={`ex: Dog, Lion, Goldfish`}
                ></textarea>
              </div>
              {errors?.animalType && <div className="text-left text-red-500 text-xs italic">{errors?.animalType}</div>}
            </div>
          )}

          {characterOutlineByUser?.characterType === StoryCharacterEnum.fictional && (
            <div className="flex flex-col gap-1">
              <div className="flex gap-2 justify-start items-center">
                <label htmlFor="character" className="w-40 text-start text-sm font-bold text-storyblue">
                  Character
                  <span className="text-red-500  text-xs italic ">* </span>
                </label>
                <textarea
                  name="character"
                  rows={2}
                  className="w-full max-h-10 rounded-md text-sm p-2 border border-gray-300"
                  value={characterOutlineByUser.character || ''}
                  required
                  onChange={(e) => handleInputChange(e)}
                  onBlur={() => onBlurSave?.()}
                  placeholder={`ex: Dragon, Superhero, Fairy`}
                ></textarea>
              </div>
              {errors?.character && <div className="text-left text-red-500 text-xs italic">{errors?.character}</div>}
            </div>
          )}

          <div className="flex flex-col gap-1">
            <div className="flex gap-2 justify-start items-center">
              <label htmlFor="mics" className="w-40 text-start text-sm font-bold text-storyblue">
                {characterOutlineByUser?.characterType === StoryCharacterEnum.human
                  ? 'Character Appearance: '
                  : 'Description: '}
                <span className="text-red-500  text-xs italic ">* </span>

              </label>
              <textarea
                name="mics"
                rows={5}
                className={`w-full max-h-32 rounded-md text-sm p-1 border text-justify ${
                  hasModerationError || errors?.mics
                    ? 'border-red-500 border-2 bg-red-50/40'
                    : 'border-gray-300'
                }`}
                value={characterOutlineByUser?.mics || ''}
                required={
                  characterOutlineByUser?.characterType !== StoryCharacterEnum.human || continueWithoutImage
                }
                onChange={(e) => handleInputChange(e)}
                onBlur={() => onBlurSave?.()}
                placeholder={` ${characterOutlineByUser?.characterType === StoryCharacterEnum.human
                  ? 'Include hair, eyes, outfit, ethnicity. e.g. Mexican, tanned skin, brown eyes, spiky brown hair, wearing glasses, a Jedi outfit, and holding a lightsaber.'
                  : characterOutlineByUser?.characterType === StoryCharacterEnum.animal
                    ? 'ex: Blue eyes, spots, stripes'
                    : characterOutlineByUser?.characterType === StoryCharacterEnum.fictional
                      ? 'ex: Spiderman, with a cape, holding a lightsaber'
                      : ''
                }`}
              ></textarea>
            </div>
            {errors?.mics && <div className="text-left text-red-500 text-xs italic">{errors?.mics}</div>}
          </div>

          {/*{!(*/}
          {/*  characterOutlineByUser?.characterType === StoryCharacterEnum.animal ||*/}
          {/*  characterOutlineByUser?.characterType === StoryCharacterEnum.fictional*/}
          {/*) && (*/}
          {/*  <>*/}
          {/*    <div className="flex flex-col gap-1">*/}
          {/*      <div className="flex gap-2 justify-start items-center">*/}
          {/*        <label htmlFor="skinTone" className="w-40 text-start text-sm text-storyblue">*/}
          {/*          Skin Tone:*/}
          {/*        </label>*/}
          {/*        <select*/}
          {/*          id="skinTone"*/}
          {/*          name="skinTone"*/}
          {/*          value={characterOutlineByUser?.skinTone || ''}*/}
          {/*          required={continueWithoutImage}*/}
          {/*          onChange={(e) => {*/}
          {/*            handleInputChange(e);*/}
          {/*          }}*/}
          {/*          className="w-full h-9 text-center border-gray-300 border rounded-md focus:ring-indigo-500 focus:border-indigo-500"*/}
          {/*        >*/}
          {/*          <option value=""></option>*/}
          {/*          {generateListOptions(skinToneOptions)}*/}
          {/*        </select>*/}
          {/*      </div>*/}
          {/*      {errors?.skinTone && <div className="text-red-500 text-left text-xs italic">{errors?.skinTone}</div>}*/}
          {/*    </div>*/}
          {/*    <div className="flex flex-col gap-1">*/}
          {/*      <div className="flex gap-2 justify-start items-center">*/}
          {/*        <label htmlFor="hairColor" className="w-40 text-start text-sm text-storyblue">*/}
          {/*          Hair Color:*/}
          {/*        </label>*/}
          {/*        <select*/}
          {/*          id="hairColor"*/}
          {/*          name="hairColor"*/}
          {/*          value={characterOutlineByUser?.hairColor || ''}*/}
          {/*          required={continueWithoutImage}*/}
          {/*          onChange={(e) => {*/}
          {/*            handleInputChange(e);*/}
          {/*          }}*/}
          {/*          className="w-full h-9 text-center border-gray-300 border rounded-md focus:ring-indigo-500 focus:border-indigo-500"*/}
          {/*        >*/}
          {/*          <option value=""></option>*/}
          {/*          {generateListOptions(heirColorOptions)}*/}
          {/*        </select>*/}
          {/*      </div>*/}
          {/*      {errors?.hairColor && <div className="text-red-500 text-left text-xs italic">{errors?.hairColor}</div>}*/}
          {/*    </div>*/}
          {/*    <div className="flex flex-col gap-1">*/}
          {/*      <div className="flex gap-2 justify-start items-center">*/}
          {/*        <label htmlFor="hairStyle" className="w-40 text-start text-sm text-storyblue">*/}
          {/*          Hair Style:*/}
          {/*        </label>*/}
          {/*        <textarea*/}
          {/*            name="hairStyle"*/}
          {/*            rows={2}*/}
          {/*            className="w-full max-h-24 rounded-md text-sm p-1 border border-gray-300 text-justify"*/}
          {/*            value={characterOutlineByUser?.hairStyle || ''}*/}
          {/*            required={continueWithoutImage}*/}
          {/*            onChange={(e) => handleInputChange(e)}*/}
          {/*            placeholder={`e.g. “curly,” “short and spiky,” “long braids,” “afro,” “ponytail”`}*/}
          {/*        ></textarea>*/}
          {/*      </div>*/}
          {/*      {errors?.hairStyle && <div className="text-red-500 text-left text-xs italic">{errors?.hairStyle}</div>}*/}
          {/*    </div>*/}
          {/*    <div className="flex flex-col gap-1">*/}
          {/*      <div className="flex gap-2 justify-start items-center">*/}
          {/*        <label htmlFor="eyeColor" className="w-40 text-start text-sm text-storyblue">*/}
          {/*          Eye Color:*/}
          {/*        </label>*/}
          {/*        <select*/}
          {/*          id="eyeColor"*/}
          {/*          name="eyeColor"*/}
          {/*          value={characterOutlineByUser?.eyeColor || ''}*/}
          {/*          required={continueWithoutImage}*/}
          {/*          onChange={(e) => {*/}
          {/*            handleInputChange(e);*/}
          {/*          }}*/}
          {/*          className="w-full h-9 text-center border-gray-300 border rounded-md focus:ring-indigo-500 focus:border-indigo-500"*/}
          {/*        >*/}
          {/*          <option value=""></option>*/}
          {/*          {generateListOptions(eyeColorOptions)}*/}
          {/*        </select>*/}
          {/*      </div>*/}
          {/*      {errors?.eyeColor && <div className="text-red-500 text-left text-xs italic">{errors?.eyeColor}</div>}*/}
          {/*    </div>*/}
          {/*    <div className="flex flex-col gap-1">*/}
          {/*      <div className="flex gap-2 justify-start items-center">*/}
          {/*        <label htmlFor="wearsUses" className="w-40 text-start text-sm text-storyblue">*/}
          {/*          Wears/Uses:*/}
          {/*        </label>*/}
          {/*        <textarea*/}
          {/*            name="wearsUses"*/}
          {/*            rows={2}*/}
          {/*            className="w-full max-h-24 rounded-md text-sm p-1 border border-gray-300 text-justify"*/}
          {/*            value={characterOutlineByUser?.wearsUses || ''}*/}
          {/*            required={continueWithoutImage}*/}
          {/*            onChange={(e) => handleInputChange(e)}*/}
          {/*            placeholder={`e.g. “glasses,” “wheelchair,” “princess dress,” “red hoodie,” “headscarf”`}*/}
          {/*        ></textarea>*/}
          {/*      </div>*/}
          {/*      {errors?.wearsUses && <div className="text-red-500 text-left text-xs italic">{errors?.wearsUses}</div>}*/}
          {/*    </div>*/}
          {/*    <div className="flex flex-col gap-1">*/}
          {/*      <div className="flex gap-2 justify-start items-center">*/}
          {/*        <label htmlFor="otherFeatures" className="w-40 text-start text-sm text-storyblue">*/}
          {/*          Other Features:*/}
          {/*        </label>*/}
          {/*        <textarea*/}
          {/*            name="otherFeatures"*/}
          {/*            rows={2}*/}
          {/*            className="w-full max-h-24 rounded-md text-sm p-1 border border-gray-300 text-justify"*/}
          {/*            value={characterOutlineByUser?.otherFeatures || ''}*/}
          {/*            required={continueWithoutImage}*/}
          {/*            onChange={(e) => handleInputChange(e)}*/}
          {/*            placeholder={`e.g. “freckles,” “birthmark,” “missing tooth,” “long eyelashes,” “dimples”`}*/}
          {/*        ></textarea>*/}
          {/*      </div>*/}
          {/*      {errors?.otherFeatures && (*/}
          {/*        <div className="text-red-500 text-left text-xs italic">{errors?.otherFeatures}</div>*/}
          {/*      )}*/}
          {/*    </div>*/}
          {/*  </>*/}
          {/*)}*/}
        </div>
      </div>

      <i className="text-red-500 whitespace-nowrap ml-4 text-xs text-left">* Required</i>
      <div className="flex gap-2 items-center justify-start px-4">
        <label
          htmlFor="mics"
          className="w-40 text-start text-sm font-bold text-storyblue whitespace-pre-wrap"
        >
          {previewImage === './dummy.png' ? 'Upload' : 'Replace'}
          <br />
          Photo:
        </label>

        <div className="w-full flex gap-2">
          {storyData?.uploadedImage !== null && storyData?.uploadedImage !== 'null' && (
            <div
              className="relative flex justify-center items-center"
              onMouseEnter={() => setHoveredOnImage(true)}
              onMouseLeave={() => setHoveredOnImage(false)}
            >
              <SecureImg
                storageSrc={storyData?.uploadedImage}
                alt="preview"
                className="w-[7rem] h-[4.5rem] rounded-xl border-white border-2 object-cover"
                style={{ filter: hoveredOnImage ? 'brightness(50%)' : 'none' }}
              />
            </div>
          )}
          <Uploader
            onFileChange={handleImageChange}
            setPreviewImage={setPreviewImage}
            previewImage={previewImage}
            selectedImage={selectedImage}
            currentPortrait={currentPortrait}
            setShouldCoverPortrait={setShouldCoverPortrait}
            warningType={'small'}
          />
        </div>
      </div>

      <div className="border-red-900 flex justify-center md:justify-end items-baseline gap-4 px-4">
        <p className="text-justify text-[11px] w-[15.5rem] italic">
          <strong>Please Note</strong>
          {continueWithoutImage
            ? ' it is not required to upload a photo, but this will improve accuracy of character.'
            : ' adding more information regarding your character provides best results.'}
        </p>
      </div>
    </div>
  );
};

export default CharacterForm;
